import type { ReactNode } from 'react';
import { splitFraction } from './primitives';

import { MoneyCell } from '@/components/primitives';
import {
  perfHaloBreakdown,
  perfHaloMetricValue,
} from '@/lib/perf-halo-metrics';
import type {
  PerfHaloEffect,
  PerfHaloMetricId,
  PerfHaloMetrics,
} from '@/lib/perf-halo-metrics';

import './attribution-breakdown.css';

export type AttributionBreakdownMetricId =
  | 'attributed_conversions'
  | 'attributed_revenue'
  | 'roas';

export function formatTickets(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  });
}

export function formatPerfHaloMetricValue(
  metrics: PerfHaloMetrics,
  metric: PerfHaloMetricId,
  effect: PerfHaloEffect = 'total',
): ReactNode {
  const value = perfHaloMetricValue(metrics, metric, effect);
  if (value === null) {
    return (
      <span className="perf-halo-unavailable" title="Not available for this denominator">
        —
      </span>
    );
  }
  if (['gross_spend', 'attributed_revenue', 'cpm', 'cpc', 'cpa'].includes(metric)) {
    return <MoneyCell value={value} dp={2}/>;
  }
  if (metric === 'ctr') return `${value.toFixed(2)}%`;
  if (metric === 'roas') return `${value.toFixed(2)}×`;
  if (metric === 'attributed_conversions') return formatTickets(value);
  return Math.trunc(value).toLocaleString('en-US');
}

function formatCompactMetric(
  metrics: PerfHaloMetrics,
  metric: AttributionBreakdownMetricId,
  effect: 'direct' | 'halo',
): string {
  const value = perfHaloMetricValue(metrics, metric, effect);
  if (value === null) return '—';
  if (metric === 'attributed_conversions') return formatTickets(value);
  if (metric === 'attributed_revenue') {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }
  return `${value.toFixed(2)}×`;
}

/** Money gets a hidden whole-dollar twin that phone KPI tiles swap in; ratios pass through. */
function withDecimals(text: ReactNode): ReactNode {
  if (typeof text !== 'string' || !text.startsWith('$')) return text;
  const [whole, fraction] = splitFraction(text);
  return fraction
    ? <><span className="acct-full">{text}</span><span className="acct-whole" aria-hidden="true">{whole}</span></>
    : text;
}

/** Total-first attribution presentation shared by Perf + Halo and Campaigns. */
export function AttributionBreakdown({
  metrics,
  metric,
  showShare = true,
  unavailableReason,
  values,
}: {
  metrics: PerfHaloMetrics;
  metric: AttributionBreakdownMetricId;
  showShare?: boolean;
  unavailableReason?: string;
  /** Server-computed ratio contributions when its denominator has its own clock. */
  values?: { total: number; direct: number; halo: number } | null;
}) {
  if (unavailableReason) {
    return <span className="perf-halo-unavailable" title={unavailableReason}>—</span>;
  }
  const breakdown = values
    ? {
        ...values,
        haloShare: values.total === 0 ? null : values.halo / values.total,
      }
    : perfHaloBreakdown(metrics, metric);
  const primary = values && metric === 'roas'
    ? `${values.total.toFixed(2)}×`
    : formatPerfHaloMetricValue(metrics, metric);
  const direct = values && metric === 'roas'
    ? `${values.direct.toFixed(2)}×`
    : formatCompactMetric(metrics, metric, 'direct');
  const halo = values && metric === 'roas'
    ? `${values.halo.toFixed(2)}×`
    : formatCompactMetric(metrics, metric, 'halo');
  return (
    <div className="perf-halo-breakdown">
      <strong>{withDecimals(primary)}</strong>
      <small>
        <span>D {withDecimals(direct)}</span>
        <span>H {withDecimals(halo)}</span>
      </small>
      {showShare && (
        <em>
          {breakdown.haloShare === null
            ? 'Halo —'
            : `Halo ${(breakdown.haloShare * 100).toFixed(1)}%`}
        </em>
      )}
    </div>
  );
}
